<template>
  <div class="boleto-view">
    <Aguarde :show="httpSend"></Aguarde>
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner"/>
      <p class="loading-text">Gerando boleto...</p>
    </div>

    <template v-else>

      <!-- Header -->
      <div class="boleto-header">
        <p class="boleto-subtitle">pagamento via</p>
        <div class="boleto-logo">
          <svg width="26" height="20" viewBox="0 0 26 20" fill="none">
            <rect x="0" y="0" width="2" height="20" fill="#1a1a2e"/>
            <rect x="3" y="0" width="1" height="20" fill="#1a1a2e"/>
            <rect x="5" y="0" width="3" height="20" fill="#1a1a2e"/>
            <rect x="9" y="0" width="1" height="20" fill="#1a1a2e"/>
            <rect x="11" y="0" width="2" height="20" fill="#1a1a2e"/>
            <rect x="14" y="0" width="1" height="20" fill="#1a1a2e"/>
            <rect x="16" y="0" width="3" height="20" fill="#1a1a2e"/>
            <rect x="20" y="0" width="1" height="20" fill="#1a1a2e"/>
            <rect x="22" y="0" width="2" height="20" fill="#1a1a2e"/>
            <rect x="25" y="0" width="1" height="20" fill="#1a1a2e"/>
          </svg>
          <span>Boleto Bancário</span>
        </div>
      </div>

      <!-- Card principal -->
      <div class="boleto-card">

        <!-- Código de Barras visual -->
        <div class="barcode-section">
          <!-- Linha digitável copia e cola -->
          <div class="copy-wrapper">
            <span class="barcode-code">{{ barcodeCode }}</span>
            <button class="copy-btn" :class="{ copied: isCopied }" @click="copyBarcodeCode">
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <template v-if="!isCopied">
                  <rect x="4" y="4" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="1.3"/>
                  <path d="M2 10V3a1 1 0 011-1h7" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
                </template>
                <template v-else>
                  <circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.3"/>
                  <path d="M4.5 7l2 2 3-3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"
                        stroke-linejoin="round"/>
                </template>
              </svg>
              {{ isCopied ? 'copiado!' : 'copiar' }}
            </button>
          </div>

          <Transition name="fade">
            <div class="copy-feedback" v-if="isCopied">
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="6" stroke="#0F6E56" stroke-width="1.3"/>
                <path d="M4.5 7l2 2 3-3" stroke="#0F6E56" stroke-width="1.3" stroke-linecap="round"
                      stroke-linejoin="round"/>
              </svg>
              Código copiado! Cole no app do seu banco.
            </div>
          </Transition>
        </div>

        <!-- Ações: visualizar e baixar -->
        <div class="actions-section">
          <a v-if="boletoUrl" :href="boletoUrl" target="_blank" rel="noopener noreferrer"
             class="action-btn action-btn--outline">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 7a6 6 0 1012 0A6 6 0 001 7z" stroke="currentColor" stroke-width="1.3"/>
              <circle cx="7" cy="7" r="2" stroke="currentColor" stroke-width="1.3"/>
            </svg>
            Visualizar boleto
          </a>

          <a @click="visualiza_boleto()" download class="action-btn btn btn-primary">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1v8M4 6.5l3 3 3-3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"
                    stroke-linejoin="round"/>
              <path d="M1 11h12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
            </svg>
            Baixar DUAM
          </a>
        </div>

        <!-- Divider -->
        <div class="divider"/>

        <!-- Instruções -->
        <div class="instructions-section">
          <p class="section-title">como pagar</p>

          <p class="instructions-intro">
            O pagamento pode ser realizado pelo seu banco ou em agência parceira
            (como uma casa lotérica ou supermercado) das seguintes formas:
          </p>

          <div class="steps-list">
            <div
                class="step-item"
                v-for="step in listPassos()"
                :key="step.number"
                :class="{ 'step-last': step.type === 'deadline' }"
            >
              <div class="step-badge" :class="`step-badge--${step.type}`">
                <svg v-if="step.type === 'deadline'" width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path d="M6 1v5l3 3" stroke="#92400E" stroke-width="1.5" stroke-linecap="round"/>
                  <circle cx="6" cy="6" r="5" stroke="#92400E" stroke-width="1.3"/>
                </svg>
                <span v-else>{{ step.number }}</span>
              </div>
              <p class="step-text" v-html="step.text"></p>
            </div>
          </div>
        </div>

        <!-- Aviso de compensação -->
        <div class="compensation-notice">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style="flex-shrink:0; margin-top: 1px;">
            <circle cx="7" cy="7" r="6" stroke="#1e40af" stroke-width="1.3"/>
            <path d="M7 4v3.5" stroke="#1e40af" stroke-width="1.3" stroke-linecap="round"/>
            <circle cx="7" cy="10" r="0.7" fill="#1e40af"/>
          </svg>
          <p class="notice-text">
            O pagamento por boleto tem um prazo de compensação de até
            <strong>72 horas úteis</strong> (3 dias úteis).
          </p>
        </div>

      </div>

    </template>
  </div>
</template>

<script>
import {impressao} from "@/helps/impressao";
import {getFinanceiroGuia} from "@/api/relatorio/financeiro";
import Aguarde from "@/components/widgets/aguarde.vue";

export default {
  name: 'BoletoView',
  components: {Aguarde},

  props: {
    dam_hash: {
      type: String,
      default: null,
    },
    barcodeCode: {
      type: String,
      default: null,
    },
    boletoUrl: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      httpSend: false,
      isLoading: true,
      isCopied: false,
      copyTimeout: null,
    };
  },

  watch: {
    barcodeCode(val) {
      if (val) this.isLoading = false;
    },
    boletoUrl(val) {
      if (val) this.isLoading = false;
    },
  },

  mounted() {
    if (this.barcodeCode || this.boletoUrl) {
      this.isLoading = false;
    }
  },

  beforeUnmount() {
    clearTimeout(this.copyTimeout);
  },

  methods: {
    visualiza_boleto() {
      this.httpSend = true;
      impressao(getFinanceiroGuia(this.dam_hash), 'P').catch(error => {
        console.log(error)
      }).finally(() => {
        this.httpSend = false;
      })
    },
    async copyBarcodeCode() {
      try {
        await navigator.clipboard.writeText(this.barcodeCode);
        this.isCopied = true;
        clearTimeout(this.copyTimeout);
        this.copyTimeout = setTimeout(() => {
          this.isCopied = false;
        }, 3000);
      } catch (err) {
        console.error('Erro ao copiar:', err);
      }
    },
    listPassos() {
      return [
        {number: 1, type: 'default', text: 'Nos <strong>terminais de autoatendimento</strong>'},
        {number: 2, type: 'default', text: 'Online, via <strong>Internet Banking</strong>'},
        {number: 3, type: 'default', text: 'Pelo <strong>telefone</strong>'},
        {number: 4, type: 'default', text: 'No <strong>caixa eletrônico</strong>'},
        {number: 5, type: 'default', text: 'Via <strong>aplicativo</strong> da sua instituição financeira'},
        {
          number: 6,
          type: 'deadline',
          text: 'Realize o pagamento até a data de vencimento'
        },
      ]
    },
  },
};
</script>

<style scoped>
/* ─── Layout ─────────────────────────────────────────────── */
.boleto-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  max-width: 600px;
  margin: 0 auto;
}

/* ─── Loading ────────────────────────────────────────────── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 2.5px solid #e5e7eb;
  border-top-color: #1a1a2e;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  margin: 0;
  font-size: 13px;
  color: #9ca3af;
}

/* ─── Header ─────────────────────────────────────────────── */
.boleto-header {
  text-align: center;
}

.boleto-subtitle {
  margin: 0 0 4px;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #9ca3af;
  font-weight: 500;
}

.boleto-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.boleto-logo span {
  font-size: 18px;
  font-weight: 500;
  color: #111827;
  letter-spacing: -0.02em;
}

/* ─── Card ───────────────────────────────────────────────── */
.boleto-card {
  width: 100%;
  background: #ffffff;
  border: 0.5px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

/* ─── Código de Barras ───────────────────────────────────── */
.barcode-section {
  padding: 1.75rem 1.75rem 1.5rem;
  border-bottom: 0.5px solid #e5e7eb;
}

.barcode-label {
  margin: 0 0 12px;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9ca3af;
  font-weight: 500;
}

.barcode-visual {
  background: #f9fafb;
  border: 0.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px 12px;
  margin-bottom: 14px;
  overflow: hidden;
}

.copy-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f9fafb;
  border: 0.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
}

.barcode-code {
  flex: 1;
  font-size: 11px;
  font-family: monospace;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.copy-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  background: #32BCAD;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

.copy-btn:hover {
  background: #26a99b;
}

.copy-btn.copied {
  background: #0F6E56;
}

.copy-btn.copied {
  background: #0F6E56;
}

.copy-feedback {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 10px;
  background: #E1F5EE;
  border-radius: 8px;
  font-size: 12px;
  color: #0F6E56;
  font-weight: 500;
}

/* ─── Ações ──────────────────────────────────────────────── */
.actions-section {
  display: flex;
  gap: 10px;
  padding: 1.25rem 1.75rem;
  border-bottom: 0.5px solid #e5e7eb;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  flex: 1;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s;
}

.action-btn--outline {
  background: transparent;
  border: 0.5px solid #d1d5db;
  color: #374151;
}

.action-btn--outline:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.action-btn--solid {
  background: #1a1a2e;
  border: 0.5px solid #1a1a2e;
  color: white;
}

.action-btn--solid:hover {
  background: #2d2d4e;
  border-color: #2d2d4e;
}

/* ─── Divider ────────────────────────────────────────────── */
.divider {
  height: 0.5px;
  background: #e5e7eb;
}

/* ─── Instruções ─────────────────────────────────────────── */
.instructions-section {
  padding: 1.5rem 1.75rem 0;
}

.section-title {
  margin: 0 0 10px;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9ca3af;
  font-weight: 500;
}

.instructions-intro {
  margin: 0 0 1rem;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
}

.steps-list {
  display: flex;
  flex-direction: column;
}

.step-item {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 0.5px solid #f3f4f6;
}

.step-item.step-last {
  border-bottom: none;
}

.step-badge {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
  font-size: 11px;
  font-weight: 500;
}

.step-badge--default {
  background: #e8eaf6;
  color: #3730a3;
}

.step-badge--deadline {
  background: #FEF3C7;
}

.step-text {
  margin: 0;
  font-size: 13px;
  color: #111827;
  line-height: 1.5;
}

.step-text :deep(strong) {
  font-weight: 500;
}

.step-text :deep(.deadline-date) {
  font-weight: 500;
  color: #92400E;
}

/* ─── Aviso de compensação ───────────────────────────────── */
.compensation-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 1.25rem 1.75rem 1.5rem;
  padding: 10px 14px;
  background: #eff6ff;
  border: 0.5px solid #bfdbfe;
  border-radius: 8px;
}

.notice-text {
  margin: 0;
  font-size: 12px;
  color: #1e40af;
  line-height: 1.6;
}

.notice-text strong {
  font-weight: 500;
}

/* ─── Animações ──────────────────────────────────────────── */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>